import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Grid, FormControl, Dialog, DialogTitle, DialogContent,
  DialogActions, Select, MenuItem, TablePagination, Typography, Autocomplete, TextField
} from "@mui/material";
import PageTitle from "../../../../common/PageTitle";
import Loader from "../../../../common/Loader";
import BasicSelector from "../../../../common/Selector";
import LocalDateSelector from "../../../../common/LocalDateSelector";
import EmptyTable from "../../../../common/EmptyTable";
import { getAPI, patchAPI } from "../../../../network";
import {
  formatDateUTCtoLocal,
  differenceOf2DatesInMinutes,
  getDateFromStringDateAndTime,
  tableHeader,
  tableData
} from "../../../../utils";
import { MapContainer, Marker, Popup, TileLayer, Polyline } from 'react-leaflet';
import { useParams } from "react-router-dom";
import L from 'leaflet';
import { Circle } from 'react-leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});
export default function TimeAndAttendanceRecords() {
  const { siteId } = useParams();
  const companyId = localStorage.getItem("companyIdLS");

  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState({ userId: "", startDate: null, endDate: null });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loader, setLoader] = useState(false);

  const [openSettings, setOpenSettings] = useState(false);
  const [clockOutDistance, setClockOutDistance] = useState(50);
  const [checkOutLimit, setCheckOutLimit] = useState(15);

  const [openMap, setOpenMap] = useState(false);
  const [position1, setPosition1] = useState(null);
  const [position2, setPosition2] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [siteMarkers, setSiteMarkers] = useState([]);
  const [mapType, setMapType] = useState("street");

  const distanceOptions = Array.from({ length: 10 }, (_, i) => ({
    label: `${(i + 1) * 50} meters`,
    value: (i + 1) * 50,
  }));

  const timeOptions = Array.from({ length: 240 / 15 }, (_, i) => ({
    label: `${(i + 1) * 15} minutes`,
    value: (i + 1) * 15,
  }));
  useEffect(() => {
    fetchData();
  }, [siteId]);

  const fetchData = async () => {
    setLoader(true);
    try {
      const url = companyId ? `/timeAndAttendance?companyId=${companyId}&siteId=${siteId}` : `/timeAndAttendance`;
      const data = await getAPI(url);
      if (data) {
        setRecords(data);
        extractUsers(data);
      }
      const settings = await getAPI(`/sites/details/${siteId}`);
      if (settings) {
        setClockOutDistance(settings.clockOutDistanceRange || 50);
        setCheckOutLimit(settings.forceCheckOutLimit || 15);
      }
    } catch (err) {
      console.error("Error:", err);
    }
    setLoader(false);
  };

  const extractUsers = (records) => {
    const unique = new Map();
    records.forEach(item => {
      const user = item?.assignedUser;
      if (user && !unique.has(user._id)) {
        unique.set(user._id, {
          label: `${user.firstname} ${user.lastname}`,
          value: user._id
        });
      }
    });
    setUsers([...unique.values()]);
  };

  const applyFilters = useMemo(() => {
    let filtered = [...records];
    const { userId, startDate, endDate } = filter;
    if (userId) filtered = filtered.filter(r => r?.assignedUser?._id === userId);
    if (startDate) filtered = filtered.filter(r => new Date(r.shiftDate) >= new Date(startDate).setHours(0, 0, 0, 0));
    if (endDate) filtered = filtered.filter(r => new Date(r.shiftDate) <= new Date(endDate).setHours(23, 59, 59, 999));
    return filtered;
  }, [filter, records]);

  const handleFilterChange = (type, value) => {
    setFilter(prev => ({ ...prev, [type]: value }));
    setPage(0);
  };

  const handleOpenMap = (record, type) => {
    const isClockIn = type === 'in';
    const lat = isClockIn ? record.clockInLocationLat : record.clockOutLocationLat;
    const lng = isClockIn ? record.clockInLocationLng : record.clockOutLocationLng;
    const siteLat = record.siteId?.latitude;
    const siteLng = record.siteId?.longitude;

    if (!lat || !lng || !siteLat || !siteLng) return;

    setPosition1([lat, lng]);
    setPosition2([siteLat, siteLng]);
    setMarkers([{ coordinates: [lat, lng], name: `${record.assignedUser.firstname} ${record.assignedUser.lastname}` }]);
    setSiteMarkers([{ coordinates: [siteLat, siteLng], name: record.siteId.name }]);
    setOpenMap(true);
  };
  const renderStatus = (matched, distance, onClick) =>
    matched ? <Typography color="green">✔ Matched</Typography> :
      <Button variant="outlined" color="error" onClick={onClick}>
        ❌ Mismatch ({distance ?? "?"} km)
      </Button>;

  return (
    <Box>
      <Loader loader={loader} />
      <PageTitle title="Time and Attendance Records" />

      {/* Filters */}
      <Grid container spacing={2} my={2}>
        <Grid item xs={4}>
          <Autocomplete
            options={[{ label: "All", value: "" }, ...users]}
            value={[{ label: "All", value: "" }, ...users].find(u => u.value === filter.userId) || null}
            getOptionLabel={(option) => option.label}
            onChange={(_, newValue) => handleFilterChange("userId", newValue?.value || "")}
            renderInput={(params) => (
              <TextField {...params} label="Select Resource" variant="outlined" />
            )}
            isOptionEqualToValue={(option, value) => option.value === value.value}
          />
        </Grid>
        <Grid item xs={4}><LocalDateSelector title="Start Date" value={filter.startDate} onChange={(date) => handleFilterChange("startDate", date)} /></Grid>
        <Grid item xs={4}><LocalDateSelector title="End Date" value={filter.endDate} onChange={(date) => handleFilterChange("endDate", date)} /></Grid>
      </Grid>
      <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2} sx={{ mb: 2 }}>
        <Button variant="outlined" color="secondary" onClick={() => {
          setFilter({ userId: "", startDate: null, endDate: null });
          setPage(0);
        }}>
          Clear Filters
        </Button>
        <Button variant="contained" onClick={() => setOpenSettings(true)}>
          Settings
        </Button>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {["Date", "Site", "Shift Type", "Name", "Shift Code", "Start", "End", "Clock In At", "Late/Early", "Missed Clocked In", "Clock In Status", "Clock Out Status", "Clock Out At", "Auto Clock Out"].map(h => (
                <TableCell key={h} sx={tableHeader}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {applyFilters.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r) => (
              <TableRow key={r._id}>
                <TableCell sx={tableData}>{r.shiftDate?.split("T")[0]}</TableCell>
                <TableCell sx={tableData}>{r.siteId?.name}</TableCell>
                <TableCell sx={tableData}>{r.shiftType === "Fixed" ? "Rostered" : "Casual"}</TableCell>
                <TableCell sx={tableData}>{`${r?.assignedUser?.firstname} ${r?.assignedUser?.lastname}`}</TableCell>
                <TableCell sx={tableData}>{r.shiftCode}</TableCell>
                <TableCell sx={tableData}>{r.startTime}</TableCell>
                <TableCell sx={tableData}>{r.endTime}</TableCell>
                <TableCell sx={tableData}>{r.clockInTime ? formatDateUTCtoLocal(r.clockInTime) : "-"}</TableCell>
                <TableCell sx={tableData}>
                  {r.clockInTime ? (() => {
                    const mins = differenceOf2DatesInMinutes(r.clockInTime, getDateFromStringDateAndTime(r.shiftDate?.split("T")[0], r.startTime));
                    return mins === 0 ? "On Time" : mins > 0 ? `${mins} min late` : `${Math.abs(mins)} min early`;
                  })() : "-"}
                </TableCell>
                <TableCell sx={tableData}>{r.clockInTime ? "No" : "Yes"}</TableCell>
                <TableCell sx={tableData}>{r.clockInTime ? renderStatus(r.isMatch, r.distance, () => handleOpenMap(r, 'in')) : "-"}</TableCell>
                <TableCell sx={tableData}>{r.clockOutTime ? r.autoClockOut ? "Auto Clock out" : renderStatus(r.isMatch2, r.distance2, () => handleOpenMap(r, 'out')) : "-"}</TableCell>
                <TableCell sx={tableData}>{r.clockOutTime ? formatDateUTCtoLocal(r.clockOutTime) : "-"}</TableCell>
                <TableCell sx={tableData}>{r.autoClockOut ? "Yes" : "No"}</TableCell>
              </TableRow>
            ))}
            {applyFilters.length === 0 && <EmptyTable colSpan={14} message="No records found." />}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          count={applyFilters.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>
      <Dialog open={openSettings} onClose={() => setOpenSettings(false)} fullWidth>
        <DialogTitle>Site Settings</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <Typography variant="caption" color="text.secondary">Clock Out Distance</Typography>
            <Select value={clockOutDistance} onChange={(e) => setClockOutDistance(e.target.value)}>
              {distanceOptions.map((item, i) => (
                <MenuItem key={i} value={item.value}>{item.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <Typography variant="caption" color="text.secondary">Force Check Out Limit</Typography>
            <Select value={checkOutLimit} onChange={(e) => setCheckOutLimit(e.target.value)}>
              {timeOptions.map((item, i) => (
                <MenuItem key={i} value={item.value}>{item.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpenSettings(false)}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            try {
              await patchAPI(`/sites/${siteId}`, {
                clockOutDistanceRange: clockOutDistance,
                forceCheckOutLimit: checkOutLimit
              });
              setOpenSettings(false);
            } catch (err) {
              console.error("Failed to update settings", err);
            }
          }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Dialog */}
      <Dialog open={openMap} onClose={() => setOpenMap(false)} maxWidth="xl">
        <DialogTitle style={{ marginButton: '20px' }}>
          Location Mismatch
          <Button onClick={() => setMapType(prev => prev === 'street' ? 'satellite' : 'street')} sx={{ float: 'right' }}>
            Switch to {mapType === 'street' ? 'Satellite' : 'Street'}
          </Button>
        </DialogTitle>
        <DialogContent>
          {position1 && position2 && (
            <MapContainer center={position2} zoom={18} style={{ height: '80vh', width: '120vh', marginTop: '2vh' }}>
              <TileLayer url={mapType === "street"
                ? "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                : "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              } />
              {[...markers, ...siteMarkers].map((m, i) => (
                <Marker key={i} position={m.coordinates}><Popup>{m.name}</Popup></Marker>
              ))}
              {position2 && (
                <Circle
                  center={position2}
                  radius={clockOutDistance} // this is in meters
                  pathOptions={{ color: 'blue', fillColor: 'rgba(0,0,255,0.1)', fillOpacity: 0.3 }}
                />
              )}

              <Polyline positions={[position1, position2]} color="red" />
            </MapContainer>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
